import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Layout from "@/components/Layout";
import {
  MessageSquare, FileText, Bell, Scale, Search, GitCompare,
  Folder, FileSignature, Languages, Workflow, BookOpen,
  GitBranch, ChevronRight, Clock, TrendingUp, Users, Zap,
  CheckSquare, BarChart3, AlertTriangle, Timer
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const featureGroups = [
  {
    label: "Intelligence IA",
    color: "blue",
    items: [
      { icon: MessageSquare, title: "Assistant Juridique", desc: "Questions & réponses avec citations", path: "/chat", testId: "dash-chat" },
      { icon: BookOpen, title: "Knowledge", desc: "Légifrance, EUR-Lex, jurisprudence", path: "/knowledge", testId: "dash-knowledge" },
      { icon: Bell, title: "Veille Légale", desc: "Surveillance automatique des lois", path: "/legal-watch", testId: "dash-legal-watch" },
    ]
  },
  {
    label: "Documents",
    color: "emerald",
    items: [
      { icon: FileText, title: "Analyse", desc: "Score de risque, clauses sensibles", path: "/documents", testId: "dash-documents" },
      { icon: GitCompare, title: "Comparaison", desc: "Diff intelligent entre 2 documents", path: "/document-comparison", testId: "dash-comparison" },
      { icon: Folder, title: "Traitement en masse", desc: "Analyse jusqu'à 40 documents", path: "/bulk-processing", testId: "dash-bulk" },
    ]
  },
  {
    label: "Rédaction",
    color: "violet",
    items: [
      { icon: FileSignature, title: "Contrats", desc: "Bibliothèque de clauses + templates", path: "/contracts", testId: "dash-contracts" },
      { icon: Languages, title: "Traduction", desc: "Texte et documents via DeepL", path: "/translation", testId: "dash-translation" },
      { icon: Search, title: "Recherche sémantique", desc: "Cherchez dans vos documents", path: "/search", testId: "dash-search" },
    ]
  },
  {
    label: "Automatisation",
    color: "amber",
    items: [
      { icon: Workflow, title: "Workflows", desc: "Automatisez vos processus", path: "/workflows", testId: "dash-workflows" },
      { icon: GitBranch, title: "Workflow Builder", desc: "Créez des workflows visuels", path: "/workflow-builder", testId: "dash-wf-builder" },
      { icon: CheckSquare, title: "Mes Tâches", desc: "Gérez vos délais et audiences", path: "/todos", testId: "dash-todos" },
    ]
  },
];

const colorMap = {
  blue: { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-200", card: "hover:border-blue-300" },
  emerald: { bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-200", card: "hover:border-emerald-300" },
  violet: { bg: "bg-violet-100", text: "text-violet-700", border: "border-violet-200", card: "hover:border-violet-300" },
  amber: { bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-200", card: "hover:border-amber-300" },
};

export default function Dashboard({ user, cabinet, onLogout }) {
  const navigate = useNavigate();
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [recentDossiers, setRecentDossiers] = useState([]);
  const [docLoading, setDocLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [todoStats, setTodoStats] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('lawliet_token');
    const headers = { Authorization: `Bearer ${token}` };
    const loadAll = async () => {
      try {
        const [docsRes, analyticsRes, todosRes] = await Promise.allSettled([
          axios.get(`${API}/documents`, { headers }),
          axios.get(`${API}/analytics/dashboard?days=30`, { headers }),
          axios.get(`${API}/todos/stats`, { headers }),
        ]);
        if (docsRes.status === 'fulfilled') setRecentDocuments((docsRes.value.data || []).slice(0, 5));
        if (analyticsRes.status === 'fulfilled') setAnalytics(analyticsRes.value.data);
        if (todosRes.status === 'fulfilled') setTodoStats(todosRes.value.data);
      } catch { /* silent */ }

      // Fetch recent dossiers
      try {
        const dossiersRes = await axios.get(`${BACKEND_URL}/api/dossiers`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRecentDossiers((dossiersRes.data || []).slice(0, 4));
      } catch (err) {
        console.error('Erreur chargement dossiers:', err);
      }

      finally { setDocLoading(false); }
    };
    loadAll();
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const diff = Math.floor((Date.now() - date) / 86400000);
    if (diff === 0) return "Aujourd'hui";
    if (diff === 1) return "Hier";
    if (diff < 7) return `Il y a ${diff} j.`;
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };

  return (
    <Layout user={user} cabinet={cabinet} onLogout={onLogout}>
      <div className="space-y-8" data-testid="dashboard">
        {/* Welcome Banner */}
        <div className="hero-authority ta-rise rounded-2xl p-8 text-white shadow-[0_18px_40px_-20px_rgba(30,58,138,0.55)]">
          <div className="relative flex items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-white/15 ring-1 ring-white/20 p-2.5 rounded-xl backdrop-blur-sm">
                  <Scale className="h-7 w-7" />
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-gold-200">
                    {cabinet?.name || user.organization || "Votre cabinet"}
                  </p>
                  <h1 className="font-serif text-3xl font-semibold leading-tight tracking-tight">
                    Bonjour, {user.full_name.split(' ')[0]}
                  </h1>
                </div>
              </div>
              <p className="text-blue-100/90 max-w-lg leading-relaxed">
                Votre plateforme d'intelligence juridique. Analysez, rédigez, recherchez — tout en un.
              </p>
            </div>
            <div className="hidden lg:flex flex-col items-end gap-2">
              {["Mistral Large", "RGPD Conforme", "Hébergé en EU"].map((b) => (
                <span key={b} className="inline-flex items-center gap-1.5 bg-white/12 ring-1 ring-white/20 text-white/95 text-xs font-medium px-3 py-1 rounded-full backdrop-blur-sm">
                  <span className="h-1.5 w-1.5 rounded-full bg-gold-300" />
                  {b}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Actions — All Features */}
        <div className="space-y-7">
          {featureGroups.map((group, gi) => {
            const c = colorMap[group.color];
            return (
              <div key={group.label}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="accent-bar text-xs font-bold uppercase tracking-[0.12em] text-slate-700">{group.label}</span>
                  <div className="flex-1 h-px bg-gradient-to-r from-slate-200 to-transparent" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                  {group.items.map((item, ii) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        data-testid={item.testId}
                        className="card-surface card-interactive ta-rise text-left p-4 group"
                        style={{ animationDelay: `${gi * 60 + ii * 40}ms` }}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`${c.bg} p-2.5 rounded-xl flex-shrink-0 ring-1 ring-inset ${c.border}`}>
                            <Icon className={`h-5 w-5 ${c.text}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-semibold text-slate-900">{item.title}</p>
                              <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5 truncate">{item.desc}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Row: Recent Documents + Analytics + Quick Tips */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Recent Documents */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="h-4 w-4 text-slate-500" />
                    Documents récents
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/documents')} className="text-blue-700 text-xs">
                    Voir tout →
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {docLoading ? (
                  <div className="space-y-2">
                    {[1,2,3].map(i => (
                      <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />
                    ))}
                  </div>
                ) : recentDocuments.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <FileText className="h-10 w-10 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">Aucun document encore uploadé</p>
                    <Button size="sm" className="mt-3 bg-blue-600" onClick={() => navigate('/documents')}>
                      Uploader un document
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {recentDocuments.map((doc) => (
                      <button
                        key={doc.id}
                        onClick={() => navigate('/documents')}
                        className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition group text-left"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className="h-4 w-4 text-blue-600 flex-shrink-0" />
                          <span className="text-sm font-medium text-slate-800 truncate">{doc.filename}</span>
                          {doc.analysis && (
                            <Badge
                              className={`text-xs flex-shrink-0 ${
                                (100 - doc.analysis.risk_score) >= 70 ? 'bg-green-100 text-green-800' :
                                (100 - doc.analysis.risk_score) >= 30 ? 'bg-amber-100 text-amber-800' :
                                'bg-red-100 text-red-800'
                              }`}
                            >
                              {100 - doc.analysis.risk_score}/100
                            </Badge>
                          )}
                        </div>
                        <span className="text-xs text-slate-400 flex-shrink-0 ml-2">{formatDate(doc.uploaded_at)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Analytics Card */}
            {analytics && (
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-slate-500" />
                    Analytics Cabinet — 30 derniers jours
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-900">{analytics.document_stats?.documents_analyzed || 0}</p>
                      <p className="text-xs text-slate-600 mt-0.5">Docs analysés</p>
                    </div>
                    <div className="text-center p-3 bg-red-50 rounded-lg">
                      <p className="text-2xl font-bold text-red-700">{analytics.risk_stats?.high_risk_count || 0}</p>
                      <p className="text-xs text-slate-600 mt-0.5">Risques élevés</p>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-700">{analytics.time_savings?.total_hours_saved?.toFixed(1) || "0.0"}</p>
                      <p className="text-xs text-slate-600 mt-0.5">Heures économisées</p>
                    </div>
                    <div className="text-center p-3 bg-violet-50 rounded-lg">
                      <p className="text-2xl font-bold text-violet-700">{analytics.total_ai_queries || 0}</p>
                      <p className="text-xs text-slate-600 mt-0.5">Requêtes IA</p>
                    </div>
                  </div>
                  {analytics.risk_stats?.recurring_risks?.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-semibold text-slate-600 mb-2">Risques récurrents :</p>
                      <div className="flex flex-wrap gap-2">
                        {analytics.risk_stats.recurring_risks.slice(0, 4).map((r, i) => (
                          <span key={i} className="flex items-center gap-1 text-xs bg-amber-50 text-amber-800 border border-amber-200 px-2 py-1 rounded-full">
                            <AlertTriangle className="h-3 w-3" /> {r.type} ({r.count})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column: Todo Stats + Quick Actions + System Info */}
          <div className="space-y-4">
            {/* Todo Stats */}
            {todoStats && (
              <Card className="border-amber-200 bg-amber-50" onClick={() => navigate('/todos')} style={{cursor:'pointer'}}>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <CheckSquare className="h-4 w-4 text-amber-700" />
                      <p className="text-sm font-semibold text-amber-900">Mes tâches</p>
                    </div>
                    <span className="text-xs text-amber-700">Voir tout →</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-xl font-bold text-slate-800">{todoStats.todo}</p>
                      <p className="text-xs text-slate-600">À faire</p>
                    </div>
                    <div>
                      <p className="text-xl font-bold text-blue-700">{todoStats.in_progress}</p>
                      <p className="text-xs text-slate-600">En cours</p>
                    </div>
                    <div>
                      <p className={`text-xl font-bold ${todoStats.overdue > 0 ? 'text-red-700' : 'text-slate-400'}`}>{todoStats.overdue}</p>
                      <p className="text-xs text-slate-600">En retard</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Widget Dossiers récents */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-900">Dossiers récents</h2>
                <button
                  onClick={() => navigate('/dossiers')}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                >
                  Voir tous <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
              {recentDossiers.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-sm text-gray-400 mb-3">Aucun dossier pour l'instant</p>
                  <button
                    onClick={() => navigate('/dossiers')}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Créer mon premier dossier →
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {recentDossiers.map((d) => (
                    <div
                      key={d.id}
                      onClick={() => navigate(`/dossiers/${d.id}`)}
                      className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl cursor-pointer transition-colors group"
                    >
                      <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Folder className="h-4 w-4 text-blue-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{d.title}</p>
                        {d.client_name && (
                          <p className="text-xs text-gray-400 truncate">{d.client_name}</p>
                        )}
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-blue-400 transition-colors flex-shrink-0" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="pt-5">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="h-4 w-4 text-blue-700" />
                  <p className="text-sm font-semibold text-blue-900">Commencer rapidement</p>
                </div>
                <div className="space-y-2">
                  {[
                    { label: "Analyser un contrat", path: "/documents" },
                    { label: "Poser une question juridique", path: "/chat" },
                    { label: "Rechercher dans Légifrance", path: "/knowledge" },
                    { label: "Rédiger un contrat", path: "/contracts" },
                    { label: "Créer une tâche", path: "/todos" },
                  ].map(a => (
                    <button
                      key={a.path}
                      onClick={() => navigate(a.path)}
                      className="w-full text-left text-xs text-blue-800 hover:text-blue-900 flex items-center gap-1.5 py-1.5 border-b border-blue-100 last:border-0"
                    >
                      <ChevronRight className="h-3 w-3 flex-shrink-0" />
                      {a.label}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-5">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="h-4 w-4 text-slate-500" />
                  <p className="text-sm font-semibold text-slate-700">Système</p>
                </div>
                <div className="space-y-2 text-xs text-slate-600">
                  <div className="flex items-center justify-between">
                    <span>Modèle LLM</span>
                    <Badge variant="outline" className="text-xs">Mistral Large</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Traduction</span>
                    <Badge variant="outline" className="text-xs">DeepL</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Rôle</span>
                    <Badge variant="outline" className="text-xs capitalize">{user.role}</Badge>
                  </div>
                  {cabinet?.name && (
                    <div className="flex items-center justify-between">
                      <span>Cabinet</span>
                      <span className="text-slate-800 font-medium truncate max-w-24">{cabinet.name}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
